/**
 * ResultsStep - Step 4: Calculation results display.
 *
 * Calls useProsumerCalculation with form values.
 * Shows loading spinner, then results when done.
 */

import { useEffect, useCallback, useState } from 'react';
import { Stack, Text, Center, Loader, Alert, Button, Group } from '@mantine/core';
import { IconAlertCircle, IconFileSpreadsheet, IconInfoCircle } from '@tabler/icons-react';
import type { UseFormReturnType } from '@mantine/form';
import type { ProsumerFormValues } from '../../types';
import { useProsumerCalculation } from '../../hooks/useProsumerCalculation';
import { ProsumerResultsSummary } from '../results/ProsumerResultsSummary';
import { SelfConsumptionIndicator } from '../results/SelfConsumptionIndicator';
import { TariffComparisonTable } from '../results/TariffComparisonTable';
import { SelfConsumptionChart } from '../results/SelfConsumptionChart';
import { MonthlyBalanceChart } from '../results/MonthlyBalanceChart';
import { TariffRecommendationCard } from '../results/TariffRecommendationCard';
import { ROIPaybackCard } from '../results/ROIPaybackCard';
import { MojPradCard } from '../results/MojPradCard';
import { RegulatoryDisclaimers } from '../shared/RegulatoryDisclaimers';
import { downloadProsumerExcel } from '../../utils/generateProsumerExcel';

export interface ResultsStepProps {
  form: UseFormReturnType<ProsumerFormValues>;
}

export function ResultsStep({ form }: ResultsStepProps) {
  const { result, loading, error, calculate } = useProsumerCalculation();
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    calculate(form.values);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentional: calculate once on mount, not on every re-render

  const handleDownload = useCallback(async () => {
    if (!result) return;
    setDownloading(true);
    try {
      await downloadProsumerExcel(form.values, result);
    } finally {
      setDownloading(false);
    }
  }, [form.values, result]);

  if (loading) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="lg" />
          <Text c="dimmed">Obliczam...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        {error}
      </Alert>
    );
  }

  if (!result) {
    return (
      <Text c="dimmed">Brak wyników</Text>
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Text fw={600} size="lg">Wyniki</Text>
        <Button
          leftSection={<IconFileSpreadsheet size={16} />}
          variant="light"
          color="green"
          size="sm"
          loading={downloading}
          onClick={handleDownload}
        >
          Pobierz Excel
        </Button>
      </Group>

      {result.warnings && result.warnings.length > 0 && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light">
          {result.warnings.map((warning, i) => (
            <Text key={i} size="sm">{warning}</Text>
          ))}
        </Alert>
      )}

      <TariffRecommendationCard
        recommendedTariff={result.recommendedTariff}
        maxSavingsPln={result.maxSavingsPln}
        maxSavingsPercent={result.maxSavingsPercent}
        currentTariff={form.values.currentTariff}
      />

      <ProsumerResultsSummary result={result} />

      <SelfConsumptionIndicator selfConsumption={result.selfConsumption} />

      <ROIPaybackCard
        roi={result.roi}
        installationCostPln={form.values.pvConfig.installationCostPln}
        subsidyPln={result.subsidyEstimate?.eligibleAmount}
      />

      <MojPradCard subsidyEstimate={result.subsidyEstimate} />

      <TariffComparisonTable
        comparisons={result.tariffComparisons}
        recommendedTariff={result.recommendedTariff}
        currentTariff={form.values.currentTariff}
      />

      <SelfConsumptionChart selfConsumption={result.selfConsumption} />

      <MonthlyBalanceChart comparisons={result.tariffComparisons} />

      <RegulatoryDisclaimers />

      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" c="dimmed">
          Wyniki oparte na historycznych cenach energii i szacunkach produkcji PV. Rzeczywiste oszczędności mogą różnić się w zależności od cen energii, warunków atmosferycznych i zużycia energii.
        </Text>
      </Alert>
    </Stack>
  );
}
